import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import { FaCopy as _FaCopy } from "react-icons/fa";
import loadingLogo from "../../../../assets/emlogo_loading_transparent.gif";
import { bestxSummaryHtmlStyles, mdToHtml } from "./bestxSummaryFormatting";

const FaCopy = _FaCopy as unknown as FC<any>;

interface BestxSummaryTileComponentProps {
  loading :any
  summary :any
  title?: string
  subtitle?: string
  settingsPills?: string[]
  onEditSettings?: () => void
}

const BestxSummaryTileComponent: React.FunctionComponent<
  BestxSummaryTileComponentProps
> = ({
  summary,
  loading,
  title = "Trade Summary",
  subtitle = "Trade context and market narrative",
  settingsPills = [],
  onEditSettings,
}) => {
  const theme = useTheme();
  const headerLeftRef = useRef<HTMLDivElement | null>(null);
  const settingsPillsRef = useRef<HTMLDivElement | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle"
  );
  const [hideSettingsPills, setHideSettingsPills] = useState(false);
  const copyFeedbackTimerRef = useRef<number | null>(null);
  const settingsPillsKey = settingsPills.join("|");

  const colors = useMemo(
    () => ({
      Dark: theme.globallink.colors.Grey[1100],
      reportSurface: "#2A3342",
      reportText: "#E8EDF4",
      reportBorder: "#3B4657",
      reportCodeSurface: "rgba(255,255,255,0.08)",
      border: "rgba(255,255,255,0.14)",
      title: theme.globallink.colors.White[500],
      sub: theme.globallink.colors.Grey[200],
      accent: theme.globallink.colors.Blue[500],
      accentSoft: "rgba(37, 99, 235, 0.16)",
      surface: "rgba(255,255,255,0.04)",
    }),
    [theme]
  );

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      padding: "14px",
      boxSizing: "border-box",
      overflow: "hidden",
      border: `1px solid ${colors.border}`,
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
      gap: "12px",
    },
    headerRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "12px",
      flexWrap: "wrap",
    },
    headerLeft: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: 0,
      flex: 1,
      position: "relative",
    },
    title: {
      color: colors.title,
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "18px",
      margin: 0,
    },
    subtitle: {
      color: colors.sub,
      fontSize: "12px",
      margin: 0,
      opacity: 0.9,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },
    settingsPills: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "4px",
      marginTop: "4px",
    },
    settingsPill: {
      display: "inline-flex",
      alignItems: "center",
      height: "20px",
      padding: "0 7px",
      boxSizing: "border-box",
      borderRadius: "5px",
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      color: "rgba(224,224,224,0.75)",
      fontSize: "11px",
      lineHeight: "18px",
      fontWeight: 500,
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    },
    settingsPillPrimary: {
      borderColor: "rgba(96, 165, 250, 0.3)",
      background: "rgba(37, 99, 235, 0.14)",
      color: "#DCEBFF",
    },
    settingsPillsHidden: {
      position: "absolute",
      left: 0,
      right: 0,
      height: 0,
      overflow: "hidden",
      visibility: "hidden",
      pointerEvents: "none",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexShrink: 0,
      flexWrap: "wrap",
    },
    statusPill: {
      borderRadius: "999px",
      padding: "6px 10px",
      fontSize: "11px",
      fontWeight: 700,
      lineHeight: "14px",
      border: "1px solid transparent",
    },
    statusPillLoading: {
      background: "rgba(255, 193, 7, 0.14)",
      color: "#FFE08A",
      borderColor: "rgba(255, 193, 7, 0.24)",
    },
    statusPillReady: {
      background: colors.accentSoft,
      color: "#DCEBFF",
      borderColor: "rgba(96, 165, 250, 0.28)",
    },
    actionButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      borderRadius: "8px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.05)",
      color: colors.title,
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.1)",
      },
      "&:disabled": {
        opacity: 0.55,
        cursor: "not-allowed",
      },
    },
    body: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      height: "auto",
      width: "100%",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    card: {
      backgroundColor: colors.reportSurface,
      color: colors.reportText,
      borderRadius: "12px",
      padding: "16px",
      width: "100%",
      boxSizing: "border-box",
      border: `1px solid ${colors.reportBorder}`,
      boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    },
    copyFooter: {
      display: "flex",
      justifyContent: "flex-end",
    },
    loadingCard: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      padding: "14px 16px",
      borderRadius: "12px",
      border: "1px solid rgba(255,255,255,0.08)",
      background: colors.surface,
      color: "#E0E0E0",
    },
    loadingLogo: {
      width: "34px",
      height: "34px",
      flexShrink: 0,
    },
    loadingEllipsis: {
      marginLeft: "2px",
      "& span": {
        animation: "$blink 1.6s infinite both",
      },
      "& span:nth-child(2)": { animationDelay: "0.2s" },
      "& span:nth-child(3)": { animationDelay: "0.4s" },
    },
    "@keyframes blink": {
      "0%, 80%, 100%": { opacity: 0 },
      "40%": { opacity: 1 },
    },
    loadingTextGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    loadingTitle: {
      margin: 0,
      fontSize: "13px",
      fontWeight: 700,
      color: "#F5F7FA",
    },
    loadingSubtitle: {
      margin: 0,
      fontSize: "12px",
      lineHeight: "18px",
      color: "rgba(224,224,224,0.68)",
    },

    // Styles for our generated HTML
    summaryHtml: {
      ...bestxSummaryHtmlStyles,
      color: colors.reportText,
      maxWidth: "78ch",
      "& code": {
        background: colors.reportCodeSurface,
        color: colors.reportText,
      },
    },
  });

  const classes = useStyles();
  const endRef = useRef<HTMLDivElement | null>(null);

  const summaryHtml = useMemo(() => mdToHtml(summary || ""), [summary]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [loading, summaryHtml]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimerRef.current != null) {
        window.clearTimeout(copyFeedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!onEditSettings || settingsPills.length === 0) {
      setHideSettingsPills(false);
      return;
    }

    const headerLeft = headerLeftRef.current;
    const pills = settingsPillsRef.current;
    if (!headerLeft || !pills) return;

    const updatePillVisibility = () => {
      const firstPill = pills.firstElementChild as HTMLElement | null;
      const singleLineHeight = firstPill?.offsetHeight || 0;
      setHideSettingsPills(
        singleLineHeight > 0 && pills.scrollHeight > singleLineHeight
      );
    };

    updatePillVisibility();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updatePillVisibility);
      observer.observe(headerLeft);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updatePillVisibility);
    return () => window.removeEventListener("resize", updatePillVisibility);
  }, [onEditSettings, settingsPills.length, settingsPillsKey]);

  const scheduleCopyStateReset = (delayMs: number) => {
    if (copyFeedbackTimerRef.current != null) {
      window.clearTimeout(copyFeedbackTimerRef.current);
    }

    copyFeedbackTimerRef.current = window.setTimeout(() => {
      setCopyState("idle");
      copyFeedbackTimerRef.current = null;
    }, delayMs);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(summary || "");
      setCopyState("copied");
      scheduleCopyStateReset(1800);
    } catch (e) {
      console.error("Copy failed:", e);
      setCopyState("failed");
      scheduleCopyStateReset(2000);
    }
  };

  const copyButton = (
    <button
      type="button"
      className={classes.actionButton}
      onClick={copyToClipboard}
      disabled={!summary}
      title="Copy Summary"
    >
      <FaCopy />
      {copyState === "copied"
        ? "Copied"
        : copyState === "failed"
          ? "Copy Failed"
          : "Copy Summary"}
    </button>
  );

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <div ref={headerLeftRef} className={classes.headerLeft}>
          <p className={classes.title}>{title}</p>
          {!onEditSettings && subtitle && (
            <p className={classes.subtitle}>{subtitle}</p>
          )}
          {settingsPills.length > 0 && (
            <div
              ref={settingsPillsRef}
              className={`${classes.settingsPills} ${
                hideSettingsPills ? classes.settingsPillsHidden : ""
              }`}
              aria-label="Summary settings"
              aria-hidden={hideSettingsPills}
            >
              {settingsPills.map((pill, index) => (
                <span
                  key={pill}
                  className={`${classes.settingsPill} ${
                    index === 0 ? classes.settingsPillPrimary : ""
                  }`}
                >
                  {pill}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={classes.headerRight}>
          {onEditSettings ? (
            <button
              type="button"
              className={classes.actionButton}
              onClick={onEditSettings}
            >
              Edit Settings
            </button>
          ) : (
            <>
              <span
                className={`${classes.statusPill} ${
                  loading ? classes.statusPillLoading : classes.statusPillReady
                }`}
              >
                {loading ? "Generating" : "Summary Ready"}
              </span>
              {copyButton}
            </>
          )}
        </div>
      </div>

      <div className={classes.body}>
        {loading && (
          <div className={classes.loadingCard}>
            <img
              className={classes.loadingLogo}
              src={loadingLogo}
              alt=""
              decoding="async"
            />
            <div className={classes.loadingTextGroup}>
              <p className={classes.loadingTitle}>
                {summary ? "Refreshing summary" : "Building summary"}
                <span className={classes.loadingEllipsis} aria-hidden="true">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </p>
              <p className={classes.loadingSubtitle}>
                {summary
                  ? "New market data is still being added to the summary."
                  : "Loading market data for the execution window."}
              </p>
            </div>
          </div>
        )}

        {summary && (
          <>
            <div className={classes.card}>
              <div
                className={classes.summaryHtml}
                dangerouslySetInnerHTML={{ __html: summaryHtml }}
              />
            </div>
            {onEditSettings && (
              <div className={classes.copyFooter}>{copyButton}</div>
            )}
          </>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
};

export default BestxSummaryTileComponent;
